import * as React from 'react';
import * as styles from "./Likes.css";
import { IUser } from '../../interfaces/IUser';
import { ILike } from '../../interfaces/ILike';
import SocialService from '../../services/SocialService';
import { IAIcon } from '../icon/Icon';
import Picker, { Emoji, EmojiStyle } from 'emoji-picker-react';
import { IATooltip } from '../tooltip/Tooltip';
import '../../styles/BasicIA.css'
import { LocalizationService } from '../../services/LocalizationService';
import { Helper } from '../../Helper';

export interface ILikesProps {
  color: string;
  highlightColor: string;
  itemId: string;
  commentId?: string;
  user: IUser;
  tenant: string;
  component: string;
  instance: string;
  token: string;
  source: string;
  environment?: "Development" | "Test" | "Production";
  likes?: ILike[];
  likesUpdated?: (likes: ILike[]) => void;
  style?: React.CSSProperties;
  dataAutomationId?: string;
  displayVertical?: boolean;
  showDimmed?: boolean;
  reactionOptions: string[];
  showEmojiPickerExpandButton: boolean;
  showReactionEvenIfUserHasNotReacted?: string;
  isMobile?: boolean;
  hideNumber?: boolean;
}

export interface ILikesState {
  showSpinner: boolean;
  likes: ILike[];
  showPicker?: boolean;
  top?: number;
  left?: number;
  selectedReaction?: string;
  tooltipContentHeight?: number;
  showMobileReactionDetails?: boolean;
}

export class IALikes extends React.Component<ILikesProps, ILikesState> {

  private emojiPickerRef: React.RefObject<HTMLDivElement>
  private readonly localizationService: LocalizationService = new LocalizationService();
  private interval: NodeJS.Timeout;
  private id: string = Math.random().toString(36).substring(2, 15);

  constructor(props: ILikesProps) {
    super(props);
    this.state = {
      showSpinner: false,
      likes: props.likes != undefined ? props.likes : [],
    };
    this.emojiPickerRef = React.createRef();
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    if (this.props.likes == undefined) {
      this.setState({ showSpinner: true, tooltipContentHeight: this.props.likes?.length * 22 }, () => this.forceUpdate());
      SocialService.getLikes(this.props.environment, this.props.tenant, this.props.component, this.props.instance, this.props.token, this.props.itemId).then((likes: ILike[]) => {
        this.setState({ showSpinner: false, likes }, () => this.forceUpdate());
        this.props.likesUpdated(likes);
      });
    }
    document.addEventListener('mousedown', (event) => this.handleClick(event));
    document.addEventListener('scroll', () => {
      this.setState({ showPicker: false });
    });
    let scrollView = document.getElementById("spPageCanvasContent");
    if (scrollView?.parentNode?.parentNode?.parentNode) {
      scrollView?.parentNode?.parentNode?.parentNode?.addEventListener('scroll', () => {
        this.setState({ showPicker: false });
      });
    } else {
      scrollView = document.getElementById("IA_contentArea");
      scrollView?.addEventListener('scroll', () => {
        this.setState({ showPicker: false });
      });
    }
    // Long press event
    if (this.props.isMobile) {
      this.handleLongPress();
    }
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', (event) => this.handleClick(event));
    document.removeEventListener('scroll', () => {
      this.setState({ showPicker: false });
    });
    const scrollView = document.getElementById("spPageCanvasContent");
    if (scrollView?.parentNode?.parentNode?.parentNode) {
      scrollView?.parentNode?.parentNode?.parentNode?.removeEventListener('scroll', () => {
        this.setState({ showPicker: false });
      });
    }
  }

  public componentWillReceiveProps(props: ILikesProps): void {
    if (props.itemId != this.props.itemId || props.likes != this.props.likes) {
      if (props.likes == undefined) {
        this.setState({ showSpinner: true }, () => this.forceUpdate());
        SocialService.getLikes(props.environment, props.tenant, props.component, props.instance, props.token, props.itemId).then((likes: ILike[]) => {
          this.setState({ showSpinner: false, likes, tooltipContentHeight: likes?.length * 22 }, () => this.forceUpdate());
          props.likesUpdated(likes);
        });
      } else {
        this.setState({ likes: props.likes, tooltipContentHeight: props.likes?.length * 22 }, () => this.forceUpdate());
      }
    }
  }

  private handleClick(event: Event) {
    if (this.emojiPickerRef && this.emojiPickerRef.current && !this.emojiPickerRef.current.contains(event.target as Node)) {
      if (this.state.showPicker) {
        this.setState({ showPicker: false })
      }
    }
  }

  private react(reactionType: string) {
    if (this.props.showDimmed) {
      return;
    }
    if (this.state.likes != undefined && this.props.user != undefined && !this.state.showSpinner) {
      this.setState({ showSpinner: true });
      SocialService.like(this.props.environment, this.props.tenant, this.props.component, this.props.instance, this.props.token, this.props.user, reactionType, this.props.itemId, this.props.source, this.props.commentId).then((likes: ILike[]) => {
        this.setState({ likes, showSpinner: false }, () => {
          if (this.props.likesUpdated) {
            this.props.likesUpdated(likes);
          }
        });
      });
    }
  }

  private removeReaction() {
    if (this.props.showDimmed) {
      return;
    }
    if (this.state.likes != undefined && this.props.user != undefined && !this.state.showSpinner) {
      let likeId: string;
      this.setState({ showSpinner: true });
      this.state.likes?.forEach((like: ILike) => {
        if (this.props.user && (like.user.userPrincipalName == this.props.user.userPrincipalName)) {
          likeId = like.id;
        }
      });
      SocialService.unlike(this.props.environment, this.props.tenant, this.props.component, this.props.instance, this.props.token, this.props.itemId, likeId, this.props.commentId).then((likes: ILike[]) => {
        this.setState({ likes, showSpinner: false }, () => {
          if (this.props.likesUpdated) {
            this.props.likesUpdated(likes);
          }
        });
      });
    }
  }

  public getMostReactedEmoji(hasLikedWithReaction: string | undefined): string {
    if (!this.state.likes || this.state.likes?.length === 0) {
      return undefined;
    }
    const typeFrequency: { [key: string]: number } = {};
    // Count the occurrences of each type
    this.state.likes?.forEach((like) => {
      const type = !like.type || like.type === "👍" ? "1f44d" : like.type;
      typeFrequency[type] = (typeFrequency[type] || 0) + 1;
    });
    // Find the type with the highest frequency
    let mostUsedType: string | undefined = undefined;
    let maxCount = 0;
    let reactCountForUserReaction = 0;
    Object.entries(typeFrequency).forEach(([type, count]) => {
      type = !type || type === "👍" ? "1f44d" : type;
      if (type === hasLikedWithReaction) {
        reactCountForUserReaction = count;
      }
      if (count > maxCount) {
        maxCount = count;
        mostUsedType = type;
      }
    });
    return maxCount === reactCountForUserReaction && hasLikedWithReaction ? hasLikedWithReaction : mostUsedType;
  }

  private groupLikesByType(likes: ILike[]): { [key: string]: ILike[] } {
    return likes?.reduce((acc, like) => {
      const type = !like.type || like.type === "👍" ? "1f44d" : like.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(like);
      return acc;
    }, {} as { [key: string]: ILike[] });
  }

  private handleLongPress(): void {
    const LikeButtonElement = document.getElementById(`LikeButton-${this.id}`);
    // listen for the long-press event
    if (LikeButtonElement) {
      // Prevent default context menu
      LikeButtonElement.oncontextmenu = function (event) {
        event.preventDefault();
        event.stopPropagation(); // not necessary in my case, could leave in case stopImmediateProp isn't available? 
        event.stopImmediatePropagation();
        return false;
      };
      // Detect long press
      LikeButtonElement.addEventListener("touchstart", () => {
        this.interval = setTimeout(() => {
          if(this.state.likes?.length > 0) {
          this.setState({ showMobileReactionDetails: true });
          }
        }, 500);
      });
      LikeButtonElement.addEventListener("touchend", () => {
        clearInterval(this.interval);
      });
    }
  }

  private renderReactionDetails(reactions: ILike[][], hasLikedWithReaction: string | undefined, hasLikedId): JSX.Element {
    return (
      <>
        {reactions.length > 1 &&
          <div
            className={styles.likeTooltipHeader}
            style={{
              padding: this.props.isMobile ? "10px 20px 10px 20px" : "0px 0px 5px 0px"
            }}
          >
            <div
              className={styles.likesText}
              style={{
                marginRight: 2,
                backgroundColor: this.state.selectedReaction == undefined ? "#eeeeee" : "transparent",
                borderRadius: 5,
                padding: "0px 5px",
                cursor: "pointer",
              }}
              onMouseEnter={() => {
                this.setState({ selectedReaction: undefined });
              }}
            >
              {this.localizationService.strings.CommentsAndLikes_AllReactions}
            </div>
            {reactions.map((reaction: ILike[], index: number) => {
              if (index < 4) {
                const reactionType = !reaction[0].type || reaction[0].type === "👍" ? "1f44d" : reaction[0].type;
                return (
                  <div
                    key={index}
                    className={styles.likeTooltipItem}
                    style={{
                      marginRight: index === reactions.length - 1 ? 0 : 2,
                      backgroundColor: this.state.selectedReaction != undefined && this.state.selectedReaction === (reactionType) ? "#eeeeee" : "transparent",
                      borderRadius: 5,
                      padding: "0px 5px",
                      cursor: "pointer",
                    }}
                    onMouseEnter={() => {
                      this.setState({ selectedReaction: reactionType });
                    }}
                    onClick={() => {
                      if (this.props.isMobile) {
                        this.setState({ selectedReaction: reactionType });
                      } else {
                        if (hasLikedWithReaction) {
                          SocialService.unlike(this.props.environment, this.props.tenant, this.props.component, this.props.instance, this.props.token, this.props.itemId, hasLikedId, this.props.commentId).then(() => {
                            this.react(reactionType);
                          });
                        } else {
                          this.react(reactionType);
                        }
                      }
                    }}
                  >
                    <div className={styles.likeTooltipItemEmoji}>
                      <Emoji
                        unified={reactionType}
                        emojiStyle={EmojiStyle.NATIVE}
                        size={15}
                      />
                    </div>
                    <div
                      className={styles.likesText}
                      style={{
                        fontWeight: hasLikedWithReaction != undefined && hasLikedWithReaction == (reactionType) ? "bold" : "normal"
                      }}
                    >
                      {reaction.length}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        }
        <div
          className={styles.likeTooltipContent}
          style={{
            height: this.props.isMobile ? 287 : this.state.tooltipContentHeight,
            maxHeight: this.props.isMobile ? 287 : 100
          }}
        >
          {this.state.likes?.map((like: ILike, index: number) => {
            const reactionType = !like.type || like.type === "👍" ? "1f44d" : like.type;
            if (this.state.selectedReaction == undefined || reactionType === this.state.selectedReaction) {
              return (
                <div
                  key={index}
                  className={styles.likeTooltipItem}
                  style={{
                    padding: this.props.isMobile ? "0px 20px" : "0px 0px",
                  }}
                >
                  <div
                    className={styles.likeTooltipItemName}
                    style={{
                      fontWeight: like?.user?.userPrincipalName?.toLowerCase() == this.props.user?.userPrincipalName?.toLowerCase() ? "bold" : "normal"
                    }}
                  >
                    {Helper.cleanDisplayName(like.user.displayName)}
                  </div>
                  <div className={styles.likeTooltipItemEmoji}>
                    <Emoji
                      unified={reactionType}
                      emojiStyle={EmojiStyle.NATIVE}
                      size={15}
                    />
                  </div>
                </div>
              );
            }
          })}
        </div>
      </>
    );
  }

  public render(): JSX.Element {
    let hasLikedWithReaction;
    let hasLikedId;
    if (this.state.likes && this.state.likes?.length > 0) {
      this.state.likes?.forEach((like: ILike) => {
        if (this.props.user && (like.user.userPrincipalName.toLowerCase() == this.props.user.userPrincipalName.toLowerCase())) {
          hasLikedWithReaction = !like.type || like.type === "👍" ? "1f44d" : like.type;
          hasLikedId = like.id;
          return;
        }
      });
    }
    let emojiPickerWidth = 324;
    if (this.props.reactionOptions == undefined) {
      emojiPickerWidth = 52;
    } else {
      emojiPickerWidth = (this.props.reactionOptions?.length * 34) + 12;
    }
    if (this.props.showEmojiPickerExpandButton) {
      emojiPickerWidth += 34;
    }
    const style = this.props.style ?? {};
    if (!style["--epr-emoji-size"]) {
      style["--epr-emoji-size"] = "24px";
    }
    let reactions: ILike[][] = [];
    if (this.state.likes?.length > 0) {
      const groupedLikes = this.groupLikesByType(this.state.likes);
      Object.keys(groupedLikes).forEach(type => {
        reactions.push(groupedLikes[type]);
      });
      reactions.sort().reverse();
    }
    return (
      <>
        <IATooltip
          content="Tooltip"
          type={"bubble"}
          disabled={this.state.likes?.length === 0 || this.state.showPicker || this.props.isMobile}
          disableScroll={true}
          borderRadius={10}
          popupMaxHeight={200}
          styles={{
            textAlign: "left",
            color: "#333333",
            marginBottom: 10,
          }}
          onDismiss={() => this.setState({ selectedReaction: undefined })}
          componentContent={
            <div className={styles.likeTooltip}>
              {this.renderReactionDetails(reactions, hasLikedWithReaction, hasLikedId)}
            </div>
          }>
          <div
            className={styles.likesAndComments}
            style={{
              height: this.props.displayVertical ? 39 : 15,
              width: this.props.displayVertical ? 40 : undefined,
              marginTop: this.props.displayVertical ? 0 : 10.5,
              opacity: this.props.showDimmed ? 0.5 : undefined,
              lineHeight: "15px",
              ...this.props.style
            }}
          >
            <div
              className={[styles.likes, "IA_OnFocus"].join(" ")}
              tabIndex={0}
              style={{
                opacity: this.props.user != undefined ? 1 : 0.5,
                width: this.props.displayVertical ? 40 : undefined,
                height: this.props.displayVertical ? 37 : 20,
                flexDirection: this.props.displayVertical ? "column" : "row",
              }}
              data-automation-id={this.props.dataAutomationId}
              id={`LikeButton-${this.id}`}
              onClick={(event) => {
                if (!(this.state.showSpinner || this.props.showDimmed)) {
                  if (hasLikedWithReaction != undefined) {
                    this.removeReaction();
                  } else {
                    if (this.props.showReactionEvenIfUserHasNotReacted) {
                      if (hasLikedId) {
                        SocialService.unlike(this.props.environment, this.props.tenant, this.props.component, this.props.instance, this.props.token, this.props.itemId, hasLikedId, this.props.commentId).then(() => {
                          this.react(this.props.showReactionEvenIfUserHasNotReacted);
                        });
                      } else {
                        this.react(this.props.showReactionEvenIfUserHasNotReacted);
                      }
                    } else {
                      if ((this.props.reactionOptions == undefined || this.props.reactionOptions?.length === 0) && !this.props.showEmojiPickerExpandButton) {
                        this.react("1f44d");
                      } else if (this.props.reactionOptions?.length === 1 && !this.props.showEmojiPickerExpandButton) {
                        this.react(this.props.reactionOptions[0]);
                      } else {
                        const eventTarget: any = event.target;
                        let top;
                        let left = event.clientX - (emojiPickerWidth / 2)
                        const targetTopPosition = Math.floor(eventTarget?.getBoundingClientRect()?.y);
                        const targetBottomPosition = Math.floor(eventTarget?.getBoundingClientRect()?.y + eventTarget?.getBoundingClientRect()?.height);

                        top = targetTopPosition - 50;
                        // If content overflows window top
                        if (top < 0) {
                          top = targetBottomPosition;
                        }
                        // If content overflows window right side
                        if (left + emojiPickerWidth + 20 > window.innerWidth) {
                          left = window.innerWidth - emojiPickerWidth - 30;
                        }
                        // If content overflows window left side
                        if (left < 10) {
                          left = 10;
                        }
                        this.setState({
                          top: top,
                          left: left
                        }, () => this.setState({ showPicker: true }));
                      }
                    }
                  }
                }
              }}
              // onKeyDown={(event) => {
              //   if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
              //     this.onClick(hasLikedWithReaction != undefined, "1f44d");
              //   }
              // }}
              onMouseEnter={(event) => {
                if (!this.state.showPicker && !(this.state.showSpinner || this.props.showDimmed)) {
                  const eventTarget: any = event.target;
                  let top;
                  let left = event.clientX - (emojiPickerWidth / 2)
                  const targetTopPosition = Math.floor(eventTarget?.getBoundingClientRect()?.y);
                  const targetBottomPosition = Math.floor(eventTarget?.getBoundingClientRect()?.y + eventTarget?.getBoundingClientRect()?.height);

                  top = targetTopPosition - 50;
                  // If content overflows window top
                  if (top < 0) {
                    top = targetBottomPosition;
                  }
                  // If content overflows window right side
                  if (left + emojiPickerWidth + 20 > window.innerWidth) {
                    left = window.innerWidth - emojiPickerWidth - 30;
                  }
                  // If content overflows window left side
                  if (left < 10) {
                    left = 10;
                  }
                  this.setState({
                    top: top,
                    left: left
                  });
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 6,
                  marginTop: this.props.displayVertical ? 0 : -1
                }}
              >
                {this.state.showPicker &&
                  <div
                    ref={this.emojiPickerRef}
                    onClick={(event) => {
                      event.stopPropagation();
                      const eventTarget: any = event.target;
                      const targetTopPosition = Math.floor(eventTarget?.getBoundingClientRect()?.y);
                      let top = targetTopPosition - 50;
                      if (top + 450 > window.innerHeight) {
                        top = window.innerHeight - 460;
                        this.setState({ top }, () => this.forceUpdate());
                      }
                      if (this.state.left + 350 > window.innerWidth) {
                        const left = window.innerWidth - 360;
                        this.setState({ left }, () => this.forceUpdate());
                      }
                    }}
                    style={{
                      position: "fixed",
                      top: this.state.top,
                      left: this.state.left,
                      zIndex: 1000000,
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    {(this.props.reactionOptions != undefined || this.props.showEmojiPickerExpandButton) &&
                      <Picker
                        reactionsDefaultOpen={true}
                        emojiStyle={EmojiStyle.NATIVE}
                        searchDisabled={true}
                        onEmojiClick={(event) => {
                          this.react(event.unified);
                          this.setState({ showPicker: false });
                        }}
                        style={style}
                        reactions={this.props.reactionOptions}
                        allowExpandReactions={this.props.showEmojiPickerExpandButton != undefined ? this.props.showEmojiPickerExpandButton : false}
                      />
                    }
                  </div>
                }
                {((this.state.showSpinner || this.props.showDimmed) || (hasLikedWithReaction == undefined && this.props.showReactionEvenIfUserHasNotReacted == undefined)) &&
                  <IAIcon
                    url={this.props.reactionOptions?.length > 1 || this.props.showEmojiPickerExpandButton ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Emoji%20Add/SVG/ic_fluent_emoji_add_24_regular.svg" : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Thumb%20Like/SVG/ic_fluent_thumb_like_48_regular.svg"}
                    color={this.props.highlightColor}
                    insertAsBackgroundImage
                    size={17}
                    style={{
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      WebkitTapHighlightColor: "transparent"
                    }}
                  />
                }
                {this.state.likes?.length > 0 && !(this.state.showSpinner || this.props.showDimmed) && (hasLikedWithReaction != undefined || this.props.showReactionEvenIfUserHasNotReacted) &&
                  <div
                    className={styles.emojiTest}
                    style={{
                      height: this.props.displayVertical ? 20 : 15,
                      width: 20,
                      textAlign: "center",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Emoji
                      unified={this.props.showReactionEvenIfUserHasNotReacted ?? hasLikedWithReaction}
                      emojiStyle={EmojiStyle.NATIVE}
                      size={15}
                    />
                  </div>
                }
              </div>
              {!this.props.hideNumber &&
                <div
                  className={this.props.displayVertical ? styles.likesTextVertical : styles.likesText}
                  style={{
                    color: this.props.highlightColor,
                    fontWeight: hasLikedWithReaction == undefined ? "normal" : "bold",
                  }}
                >
                  {this.state.likes != undefined ? this.state.likes?.length : "-"}
                </div>
              }
            </div>
          </div >
        </IATooltip>
        {this.props.isMobile &&
          <>
            <div
              className={styles.mobileReactionDetailsPanelOverlay}
              style={{
                opacity: this.state.showMobileReactionDetails ? 0.3 : 0,
                bottom: this.state.showMobileReactionDetails ? 0 : -screen.height,
              }}
              onClick={() => this.setState({ showMobileReactionDetails: false })}
            />
            <div
              className={styles.mobileReactionDetailsPanelContent}
              style={{
                width: screen.width,
                bottom: this.state.showMobileReactionDetails ? 0 : -screen.height,
              }}
            >
              {this.state.showMobileReactionDetails && this.renderReactionDetails(reactions, hasLikedWithReaction, hasLikedId)}

            </div>
          </>
        }
      </>
    );
  }
}